"use client";

import * as React from "react";
import NextImage from "next/image";
import { ChevronLeft, ChevronRight, ImageOff, Play } from "lucide-react";
import LoadingSpinner from "@/components/ui/loading-spinner";
import {
  Dialog,
  DialogContent,
  DialogImageBody,
  DialogImageStage,
  DialogThumbnailButton,
  DialogThumbnailRail,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export interface PhotoModalImage {
  id?: string;
  src: string;
  alt?: string;
  thumbnailSrc?: string;
  type?: "image" | "video";
  unoptimized?: boolean;
}

export interface PhotoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  images: PhotoModalImage[];
  initialIndex?: number;
  activeIndex?: number;
  onActiveIndexChange?: (index: number) => void;
  title?: string;
  closeButtonLabel?: string;
  className?: string;
}

const SWIPE_THRESHOLD_PX = 48;
const TAP_MOVEMENT_THRESHOLD_PX = 10;
const MODAL_IMAGE_SIZES =
  "(max-width: 640px) calc(100vw - 1.5rem), (max-width: 1280px) 78vw, 1120px";
const MODAL_IMAGE_FRAME_CLASS =
  "relative flex h-[calc(100dvh_-_env(safe-area-inset-top)_-_env(safe-area-inset-bottom)_-_11rem)] w-[calc(100vw_-_1.5rem)] max-w-[calc(100vw_-_1.5rem)] items-center justify-center sm:h-[calc(100dvh_-_5rem)] sm:w-[min(78vw,1120px)] sm:max-w-[min(78vw,1120px)]";

function getHostname(url: string) {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

function canUseNextImage(src: string, unoptimized?: boolean) {
  const trimmedSrc = src.trim();

  if (
    unoptimized ||
    trimmedSrc.length === 0 ||
    trimmedSrc.startsWith("blob:") ||
    trimmedSrc.startsWith("data:")
  ) {
    return false;
  }

  if (trimmedSrc.startsWith("/")) {
    return true;
  }

  let url: URL;
  try {
    url = new URL(trimmedSrc);
  } catch {
    return false;
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return false;
  }

  const configuredSupabaseHostname = getHostname(
    process.env.NEXT_PUBLIC_SUPABASE_URL ?? ""
  );
  const isLocalImageHost =
    url.hostname === "localhost" || url.hostname === "127.0.0.1";

  if (isLocalImageHost && process.env.NODE_ENV !== "development") {
    return false;
  }

  const allowedHostnames = new Set(
    [
      configuredSupabaseHostname,
      "jlmsyojjgtnnookzxdtf.supabase.co",
      "build-a-dress.com",
      "www.build-a-dress.com",
      "placehold.co",
      "cdn.shopify.com",
      ...(process.env.NODE_ENV === "development"
        ? ["localhost", "127.0.0.1"]
        : []),
      typeof window !== "undefined" ? window.location.hostname : null,
    ].filter((hostname): hostname is string => Boolean(hostname))
  );

  const isAllowed = allowedHostnames.has(url.hostname);

  if (!isAllowed && process.env.NODE_ENV === "development") {
    console.warn(
      `[PhotoModal] "${url.hostname}" is not in the next/image allowlist; ` +
        "serving an unoptimized <img>. Add it to remotePatterns and this allowlist."
    );
  }

  return isAllowed;
}

function clampIndex(index: number, imageCount: number) {
  if (imageCount <= 0) return 0;
  return Math.min(Math.max(index, 0), imageCount - 1);
}

function isPointInsideVisiblePhoto(
  element: Element,
  clientX: number,
  clientY: number
) {
  if (!(element instanceof HTMLImageElement)) return true;

  const { left, top, width, height } = element.getBoundingClientRect();
  const { naturalWidth, naturalHeight } = element;

  // Before the image has decoded (and in DOM test environments), its intrinsic
  // dimensions are unavailable. Treat the image element as the photo so a tap
  // cannot unexpectedly dismiss the modal while it is loading.
  if (width <= 0 || height <= 0 || naturalWidth <= 0 || naturalHeight <= 0) {
    return true;
  }

  const imageScale = Math.min(width / naturalWidth, height / naturalHeight);
  const renderedWidth = naturalWidth * imageScale;
  const renderedHeight = naturalHeight * imageScale;
  const renderedLeft = left + (width - renderedWidth) / 2;
  const renderedTop = top + (height - renderedHeight) / 2;

  return (
    clientX >= renderedLeft &&
    clientX <= renderedLeft + renderedWidth &&
    clientY >= renderedTop &&
    clientY <= renderedTop + renderedHeight
  );
}

export function PhotoModal({
  open,
  onOpenChange,
  images,
  initialIndex = 0,
  activeIndex,
  onActiveIndexChange,
  title = "Image preview",
  closeButtonLabel,
  className,
}: PhotoModalProps) {
  const availableImages = React.useMemo(
    () => images.filter(image => image.src.trim().length > 0),
    [images]
  );
  const [internalIndex, setInternalIndex] = React.useState(() =>
    clampIndex(initialIndex, availableImages.length)
  );
  const [loadedSrc, setLoadedSrc] = React.useState<string | null>(null);
  const [failedSrc, setFailedSrc] = React.useState<string | null>(null);
  const pointerStartRef = React.useRef<{
    x: number;
    y: number;
    target: EventTarget | null;
  } | null>(null);
  const returnFocusRef = React.useRef<HTMLElement | null>(null);

  const selectedIndex = clampIndex(
    activeIndex ?? internalIndex,
    availableImages.length
  );
  const activeImage = availableImages[selectedIndex] ?? null;
  const activeType = activeImage?.type ?? "image";
  const hasVideo = availableImages.some(
    image => (image.type ?? "image") === "video"
  );
  const mediaLabel = hasVideo ? "media" : "image";
  const resolvedCloseButtonLabel =
    closeButtonLabel ??
    (hasVideo ? "Close media preview" : "Close image preview");
  const hasMultipleImages = availableImages.length > 1;
  const isLoading = Boolean(
    activeImage &&
    loadedSrc !== activeImage.src &&
    failedSrc !== activeImage.src
  );
  const hasFailed = Boolean(activeImage && failedSrc === activeImage.src);

  React.useEffect(() => {
    if (open && activeIndex === undefined) {
      setInternalIndex(clampIndex(initialIndex, availableImages.length));
    }
  }, [activeIndex, availableImages.length, initialIndex, open]);

  React.useEffect(() => {
    setLoadedSrc(null);
    setFailedSrc(null);
  }, [activeImage?.src]);

  const setSelectedIndex = React.useCallback(
    (nextIndex: number) => {
      const clampedIndex = clampIndex(nextIndex, availableImages.length);
      if (activeIndex === undefined) {
        setInternalIndex(clampedIndex);
      }
      onActiveIndexChange?.(clampedIndex);
    },
    [activeIndex, availableImages.length, onActiveIndexChange]
  );

  const showPrevious = React.useCallback(() => {
    if (!hasMultipleImages) return;
    setSelectedIndex(
      selectedIndex === 0 ? availableImages.length - 1 : selectedIndex - 1
    );
  }, [
    availableImages.length,
    hasMultipleImages,
    selectedIndex,
    setSelectedIndex,
  ]);

  const showNext = React.useCallback(() => {
    if (!hasMultipleImages) return;
    setSelectedIndex(
      selectedIndex === availableImages.length - 1 ? 0 : selectedIndex + 1
    );
  }, [
    availableImages.length,
    hasMultipleImages,
    selectedIndex,
    setSelectedIndex,
  ]);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    pointerStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      target: event.target,
    };
  };

  const handlePointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = pointerStartRef.current;
    pointerStartRef.current = null;

    if (!start) return;

    const deltaX = event.clientX - start.x;
    const deltaY = event.clientY - start.y;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    const startTarget = start.target instanceof Element ? start.target : null;
    const endTarget = event.target instanceof Element ? event.target : null;
    const startedOnPhoto = Boolean(
      startTarget?.closest("[data-photo-modal-image]")
    );
    const endedOnPhoto = Boolean(
      endTarget?.closest("[data-photo-modal-image]")
    );
    const isTap =
      absDeltaX <= TAP_MOVEMENT_THRESHOLD_PX &&
      absDeltaY <= TAP_MOVEMENT_THRESHOLD_PX;

    if (isTap) {
      return;
    }

    if (!hasMultipleImages) return;

    if (absDeltaX < SWIPE_THRESHOLD_PX || absDeltaX < absDeltaY) {
      return;
    }

    if (!startedOnPhoto && !endedOnPhoto) {
      return;
    }

    if (deltaX < 0) {
      showNext();
    } else {
      showPrevious();
    }
  };

  const handleStageClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target instanceof Element ? event.target : null;
    const photo = target?.closest("[data-photo-modal-image]");

    if (
      (photo &&
        isPointInsideVisiblePhoto(photo, event.clientX, event.clientY)) ||
      target?.closest("[data-photo-modal-control]")
    ) {
      return;
    }

    onOpenChange(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      showPrevious();
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      showNext();
    }
  };

  if (!open || availableImages.length === 0 || !activeImage) return null;

  const renderThumbnails = (className?: string) =>
    availableImages.map((image, index) => {
      const type = image.type ?? "image";

      return (
        <DialogThumbnailButton
          key={image.id ?? image.src}
          selected={index === selectedIndex}
          onClick={() => setSelectedIndex(index)}
          aria-label={`Show ${mediaLabel} ${index + 1} of ${availableImages.length}`}
          className={className}
        >
          {type === "video" && !image.thumbnailSrc ? (
            <span className="relative block h-full w-full bg-black">
              <video
                src={image.src}
                className="h-full w-full object-cover opacity-75"
                muted
                playsInline
                preload="metadata"
                tabIndex={-1}
                aria-hidden
              />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="flex size-7 items-center justify-center rounded-full bg-black/65 text-white shadow-sm">
                  <Play className="ml-0.5 size-3.5 fill-current" aria-hidden />
                </span>
              </span>
            </span>
          ) : (
            <span className="relative block h-full w-full">
              {canUseNextImage(
                image.thumbnailSrc ?? image.src,
                image.unoptimized
              ) ? (
                <NextImage
                  src={image.thumbnailSrc ?? image.src}
                  alt=""
                  fill
                  sizes="96px"
                  quality={75}
                  className="object-cover"
                  draggable={false}
                />
              ) : (
                // eslint-disable-next-line @next/next/no-img-element -- Photo modal accepts blob URLs and authenticated image URLs.
                <img
                  src={image.thumbnailSrc ?? image.src}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                  draggable={false}
                />
              )}
            </span>
          )}
        </DialogThumbnailButton>
      );
    });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        variant="image"
        title={title}
        aria-describedby={undefined}
        closeButtonLabel={resolvedCloseButtonLabel}
        onKeyDown={handleKeyDown}
        onOpenAutoFocus={() => {
          returnFocusRef.current =
            document.activeElement instanceof HTMLElement
              ? document.activeElement
              : null;
        }}
        onCloseAutoFocus={event => {
          const returnTarget = returnFocusRef.current;
          returnFocusRef.current = null;
          if (returnTarget?.isConnected) {
            event.preventDefault();
            returnTarget.focus();
          }
        }}
        className={className}
      >
        <DialogImageBody className="overflow-hidden bg-transparent text-white">
          {hasMultipleImages && (
            <DialogThumbnailRail
              placement="side"
              orientation="vertical"
              className="left-[max(1rem,env(safe-area-inset-left))] hidden border-white/10 bg-black/30 p-2 shadow-2xl sm:block"
              viewportClassName="justify-start"
            >
              {renderThumbnails(
                "w-16 rounded-md border-white/20 bg-white/10 ring-offset-neutral-950"
              )}
            </DialogThumbnailRail>
          )}

          <DialogImageStage
            data-testid="photo-modal-stage"
            className={cn(
              "cursor-zoom-out touch-pan-y select-none px-3 pb-[calc(env(safe-area-inset-bottom)_+_5.75rem)] pt-[calc(env(safe-area-inset-top)_+_4.75rem)] sm:px-24 sm:py-10",
              hasMultipleImages ? "sm:pl-28" : undefined
            )}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerEnd}
            onPointerCancel={() => {
              pointerStartRef.current = null;
            }}
            onClick={handleStageClick}
          >
            {hasMultipleImages && (
              <>
                <button
                  type="button"
                  onClick={showPrevious}
                  data-photo-modal-control
                  className="absolute left-[max(1rem,env(safe-area-inset-left))] top-1/2 z-20 hidden size-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white shadow-lg backdrop-blur-xl transition-colors hover:bg-white/20 sm:flex"
                  aria-label={`Previous ${mediaLabel}`}
                >
                  <ChevronLeft className="size-5" aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={showNext}
                  data-photo-modal-control
                  className="absolute right-[max(1rem,env(safe-area-inset-right))] top-1/2 z-20 hidden size-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white shadow-lg backdrop-blur-xl transition-colors hover:bg-white/20 sm:flex"
                  aria-label={`Next ${mediaLabel}`}
                >
                  <ChevronRight className="size-5" aria-hidden />
                </button>
              </>
            )}

            <div data-photo-modal-media className={MODAL_IMAGE_FRAME_CLASS}>
              {hasFailed ? (
                <div className="flex min-h-[min(70vh,560px)] w-[min(88vw,520px)] flex-col items-center justify-center rounded-xl border border-white/10 bg-white/10 p-8 text-center text-white/70 shadow-2xl backdrop-blur-xl">
                  <ImageOff className="mb-4 size-12" aria-hidden />
                  <p className="text-sm font-medium">
                    {hasVideo ? "Media unavailable" : "Image unavailable"}
                  </p>
                </div>
              ) : activeType === "video" ? (
                <>
                  <video
                    data-photo-modal-image
                    src={activeImage.src}
                    aria-label={activeImage.alt ?? "Video preview"}
                    className="block max-h-full max-w-full rounded-xl bg-black object-contain shadow-2xl"
                    controls
                    playsInline
                    preload="metadata"
                    onLoadedMetadata={() => setLoadedSrc(activeImage.src)}
                    onError={() => setFailedSrc(activeImage.src)}
                  />
                  {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/35 backdrop-blur-sm">
                      <LoadingSpinner size="lg" />
                    </div>
                  )}
                </>
              ) : (
                <>
                  {canUseNextImage(activeImage.src, activeImage.unoptimized) ? (
                    <NextImage
                      data-photo-modal-image
                      src={activeImage.src}
                      alt={activeImage.alt ?? "Image preview"}
                      fill
                      sizes={MODAL_IMAGE_SIZES}
                      quality={75}
                      className="object-contain text-transparent drop-shadow-2xl"
                      draggable={false}
                      onLoad={() => setLoadedSrc(activeImage.src)}
                      onError={() => setFailedSrc(activeImage.src)}
                    />
                  ) : (
                    // eslint-disable-next-line @next/next/no-img-element -- Photo modal accepts blob URLs and authenticated image URLs.
                    <img
                      data-photo-modal-image
                      src={activeImage.src}
                      alt={activeImage.alt ?? "Image preview"}
                      className="block h-auto max-h-full w-auto max-w-full rounded-xl object-contain shadow-2xl"
                      draggable={false}
                      onLoad={() => setLoadedSrc(activeImage.src)}
                      onError={() => setFailedSrc(activeImage.src)}
                    />
                  )}
                  {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/35 backdrop-blur-sm">
                      <LoadingSpinner size="lg" />
                    </div>
                  )}
                </>
              )}
            </div>

            {hasMultipleImages && (
              <div className="absolute bottom-[calc(env(safe-area-inset-bottom)_+_6.5rem)] left-1/2 z-20 -translate-x-1/2 rounded-full border border-white/10 bg-black/35 px-3 py-1 text-xs font-medium text-white shadow-sm backdrop-blur-xl sm:bottom-[max(1.5rem,env(safe-area-inset-bottom))]">
                {selectedIndex + 1} / {availableImages.length}
              </div>
            )}
          </DialogImageStage>

          {hasMultipleImages && (
            <DialogThumbnailRail
              placement="footer"
              className="border-white/10 bg-black/35 px-3 pb-[calc(env(safe-area-inset-bottom)_+_0.875rem)] pt-3 shadow-[0_-20px_45px_rgba(0,0,0,0.35)] backdrop-blur-xl sm:hidden"
            >
              {renderThumbnails(
                "w-16 rounded-md border-white/20 bg-white/10 ring-offset-neutral-950"
              )}
            </DialogThumbnailRail>
          )}
        </DialogImageBody>
      </DialogContent>
    </Dialog>
  );
}
